/**
 * Puesta a punto.
 *
 * QUE PREGUNTA CONTESTA ESTA PANTALLA
 * -----------------------------------
 * "¿Que tengo que dejar cargado antes de vender por primera vez?"
 *
 * El local arranca de cero (0146): no hay ventas, clientes ni caja, pero lo que
 * pasaba antes del sistema sigue siendo cierto. Hay clientes que deben, a lo
 * mejor algun proveedor al que se le debe, y plata en cada cuenta. Se carga en
 * ese orden y la plata va AL FINAL: el saldo de cada cuenta queda en lo contado
 * teniendo en cuenta lo que ya se haya movido, asi nada se cuenta dos veces.
 *
 * Con «Arrancar» la pantalla se cierra y una diferencia de plata pasa a ser un
 * arqueo, como en cualquier otro dia.
 */
import { useRef, useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CircleCheck, Rocket } from "lucide-react";
import type { Moneda } from "@tora/shared";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { Importe, InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useProveedoresConSaldo } from "@/components/proveedores/consultas";
import { api } from "@/lib/api";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { claves, useActualizarConfig, useCajas, useConfig, useDeudas } from "@/lib/queries";
import { aNumero, type Caja, type EmpresaConfig } from "@/lib/tipos";

export const Route = createFileRoute("/puesta-a-punto")({ component: PuestaAPunto });

function PuestaAPunto(): JSX.Element {
  const { data: config } = useConfig();
  const arranque = config?.config?.arranque_at ?? null;

  return (
    <Pagina ancho="lectura">
      <PageHeader
        titulo="Puesta a punto"
        icono={Rocket}
        descripcion="Lo que hay que dejar cargado antes de vender por primera vez."
      />

      {arranque ? (
        <Aviso tono="success" titulo="El sistema ya arrancó">
          Arrancó el {new Date(arranque).toLocaleString("es-AR")}. Una diferencia en la plata se
          corrige con un arqueo desde la <Link to="/caja">Caja</Link>.
        </Aviso>
      ) : (
        <div className="space-y-4">
          <Aviso titulo="Vayan en orden: la plata se cuenta al final">
            Las reparaciones y los encargos no se cargan acá: se cargan a medida que llegan.
          </Aviso>
          <PasoDatosDelLocal />
          <PasoClientes />
          <PasoProveedores />
          <PasoPlata />
          <PasoArrancar />
        </div>
      )}
    </Pagina>
  );
}

function Paso({
  numero,
  titulo,
  descripcion,
  children,
}: {
  numero: number;
  titulo: string;
  descripcion: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {numero}. {titulo}
        </CardTitle>
        <CardDescription>{descripcion}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. Datos del local
// ─────────────────────────────────────────────────────────────────────────────

function PasoDatosDelLocal(): JSX.Element {
  const { data } = useConfig();
  const c = data?.config;
  if (!c)
    return (
      <Paso numero={1} titulo="Datos del local" descripcion="Cargando…">
        {null}
      </Paso>
    );
  // `key` para que el formulario arranque con lo que ya esta guardado.
  return <FormDatosDelLocal key={c.id} config={c} />;
}

/**
 * Razon social, CUIT y email se cargan aca mismo: la pantalla de Configuracion
 * no los tiene.
 */
function FormDatosDelLocal({ config }: { config: EmpresaConfig }): JSX.Element {
  const guardar = useActualizarConfig();
  const [razonSocial, setRazonSocial] = useState(config.razon_social ?? "");
  const [cuit, setCuit] = useState(config.cuit ?? "");
  const [email, setEmail] = useState(config.email ?? "");

  const completos = Boolean(config.razon_social && config.cuit && config.email);
  const cambio =
    razonSocial.trim() !== (config.razon_social ?? "") ||
    cuit.trim() !== (config.cuit ?? "") ||
    email.trim() !== (config.email ?? "");

  function enviar(e: FormEvent): void {
    e.preventDefault();
    guardar.mutate(
      { razon_social: razonSocial.trim(), cuit: cuit.trim(), email: email.trim() },
      {
        onSuccess: () => mostrarExito("Datos del local guardados."),
        onError: (error) => mostrarError(error),
      },
    );
  }

  return (
    <Paso
      numero={1}
      titulo="Datos del local"
      descripcion="Los que salen en los comprobantes y en la tienda."
    >
      {completos && !cambio ? <Listo>Están completos.</Listo> : null}
      <form onSubmit={enviar} className="grid gap-2 sm:grid-cols-2">
        <Input
          placeholder="Razón social"
          value={razonSocial}
          onChange={(e) => setRazonSocial(e.target.value)}
          aria-label="Razón social"
        />
        <Input
          placeholder="CUIT"
          value={cuit}
          onChange={(e) => setCuit(e.target.value)}
          aria-label="CUIT"
          inputMode="numeric"
        />
        <Input
          placeholder="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Email"
        />
        <Button type="submit" cargando={guardar.isPending} disabled={!cambio}>
          Guardar
        </Button>
      </form>
    </Paso>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Clientes que deben
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Alta de corrido: nombre, telefono, cuanto debe y Enter. Por debajo son las
 * dos llamadas de siempre, el alta del cliente y su saldo inicial.
 *
 * La cuenta del cliente se abre en la moneda de la deuda: una deuda en dolares
 * se sigue contando en dolares, una en pesos queda fija en pesos.
 */
function PasoClientes(): JSX.Element {
  const qc = useQueryClient();
  const { data: deudas } = useDeudas({ con_saldo: "1" });
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [importe, setImporte] = useState<number | null>(null);
  const [moneda, setMoneda] = useState<Moneda>("ARS");
  const [guardando, setGuardando] = useState(false);
  const refNombre = useRef<HTMLInputElement>(null);

  async function agregar(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!nombre.trim() || importe === null || importe <= 0) return;
    setGuardando(true);
    try {
      const cliente = await api.post<{ id: number }>("/clientes", {
        nombre: nombre.trim(),
        ...(telefono.trim() ? { telefono: telefono.trim() } : {}),
        ...(moneda === "USD" ? { cc_modo: "USD" } : { cc_modo: "FIJA", cc_moneda: "ARS" }),
      });
      await api.post(`/clientes/${cliente.id}/ajuste`, {
        tipo: "SALDO_INICIAL",
        importe,
        moneda,
        detalle: "Deuda anterior al sistema",
      });
      mostrarExito(`${nombre.trim()} quedó cargado.`);
      setNombre("");
      setTelefono("");
      setImporte(null);
      refNombre.current?.focus();
      void qc.invalidateQueries({ queryKey: ["deudas"] });
      void qc.invalidateQueries({ queryKey: ["clientes"] });
    } catch (error) {
      mostrarError(error);
    } finally {
      setGuardando(false);
    }
  }

  const filas = deudas?.filas ?? [];

  return (
    <Paso
      numero={2}
      titulo="Clientes que deben"
      descripcion="Cada uno con lo que debe hoy. Se cargan de corrido: completar y Enter."
    >
      <form onSubmit={(e) => void agregar(e)} className="grid gap-2 sm:grid-cols-2">
        <Input
          ref={refNombre}
          placeholder="Nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          aria-label="Nombre del cliente"
          required
        />
        <Input
          placeholder="Teléfono (opcional)"
          value={telefono}
          onChange={(e) => setTelefono(e.target.value)}
          aria-label="Teléfono"
          inputMode="tel"
        />
        <InputImporte
          valor={importe}
          onValorChange={setImporte}
          moneda={moneda}
          onMonedaChange={setMoneda}
          convertirAlCambiarMoneda={false}
          placeholder="Cuánto debe"
          sinNegativos
        />
        <Button type="submit" cargando={guardando} disabled={!nombre.trim() || !importe}>
          Agregar
        </Button>
      </form>

      {filas.length > 0 ? (
        <ul className="divide-y rounded-md border text-sm">
          {filas.map((f) => (
            <li key={f.cliente_id} className="flex items-center justify-between gap-2 px-3 py-2">
              <span className="truncate">{f.nombre}</span>
              <Importe monto={aNumero(f.saldo)} moneda={f.moneda_anclaje} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Todavía no hay clientes con deuda.</p>
      )}
      {filas.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          Si alguno quedó mal, se corrige desde Clientes, con un ajuste en su cuenta.
        </p>
      ) : null}
    </Paso>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Proveedores
// ─────────────────────────────────────────────────────────────────────────────

function PasoProveedores(): JSX.Element {
  const { data } = useProveedoresConSaldo();
  const filas = data?.filas ?? [];

  return (
    <Paso
      numero={3}
      titulo="Lo que se le debe a cada proveedor"
      descripcion="Si no se le debe nada a nadie, este paso ya está."
    >
      {filas.length === 0 ? (
        <Listo>Hoy no se le debe nada a ningún proveedor.</Listo>
      ) : (
        <ul className="divide-y rounded-md border text-sm">
          {filas.map((f) => (
            <li key={f.proveedor_id} className="flex items-center justify-between gap-2 px-3 py-2">
              <span className="truncate">{f.nombre}</span>
              <span className="flex gap-3">
                {aNumero(f.saldo_ars) !== 0 ? (
                  <Importe monto={aNumero(f.saldo_ars)} moneda="ARS" />
                ) : null}
                {aNumero(f.saldo_usd) !== 0 ? (
                  <Importe monto={aNumero(f.saldo_usd)} moneda="USD" />
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-sm text-muted-foreground">
        Para cargar una deuda: Proveedores → el proveedor → Ajuste → Saldo inicial.
      </p>
      <Button asChild variant="outline" size="sm">
        <Link to="/proveedores">Abrir Proveedores</Link>
      </Button>
    </Paso>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. La plata
// ─────────────────────────────────────────────────────────────────────────────

function PasoPlata(): JSX.Element {
  const { data: cajas } = useCajas();

  return (
    <Paso
      numero={4}
      titulo="La plata de cada cuenta"
      descripcion="Contar el efectivo y mirar el saldo del banco y de cada billetera. Lo que se anote es lo que va a mostrar la cuenta."
    >
      <div className="space-y-3">
        {(cajas ?? []).map((caja) => (
          <FilaCuenta key={caja.id} caja={caja} />
        ))}
      </div>
    </Paso>
  );
}

function FilaCuenta({ caja }: { caja: Caja }): JSX.Element {
  const qc = useQueryClient();
  const [contado, setContado] = useState<number | null>(null);
  const [anotada, setAnotada] = useState(false);
  const [guardando, setGuardando] = useState(false);

  async function anotar(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (contado === null) return;
    setGuardando(true);
    try {
      await api.post(`/arranque/cuentas/${caja.id}`, { contado });
      setAnotada(true);
      setContado(null);
      void qc.invalidateQueries({ queryKey: claves.cajas });
      void qc.invalidateQueries({ queryKey: claves.totalesCaja });
    } catch (error) {
      mostrarError(error);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void anotar(e)}
      className="grid items-center gap-2 rounded-md border p-3 sm:grid-cols-[1fr_12rem_auto]"
    >
      <div className="min-w-0">
        <p className="font-medium">
          {caja.nombre} {caja.moneda}
        </p>
        <p className="flex items-center gap-1 text-sm text-muted-foreground">
          {anotada ? <CircleCheck className="h-4 w-4 text-success" aria-hidden="true" /> : null}
          Hoy muestra <Importe monto={aNumero(caja.saldo)} moneda={caja.moneda} tamano="sm" />
        </p>
      </div>
      <InputImporte
        valor={contado}
        onValorChange={setContado}
        moneda={caja.moneda}
        placeholder="Lo que hay"
        sinNegativos
      />
      <Button type="submit" variant="outline" cargando={guardando} disabled={contado === null}>
        Anotar
      </Button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. Arrancar
// ─────────────────────────────────────────────────────────────────────────────

function PasoArrancar(): JSX.Element {
  const qc = useQueryClient();
  const [confirmar, setConfirmar] = useState(false);
  const [guardando, setGuardando] = useState(false);

  async function arrancar(): Promise<void> {
    setGuardando(true);
    try {
      await api.post("/arranque/listo", {});
      mostrarExito("¡Listo! El sistema arrancó.");
      void qc.invalidateQueries({ queryKey: claves.config });
    } catch (error) {
      mostrarError(error);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Paso
      numero={5}
      titulo="Arrancar"
      descripcion="Cuando esté todo cargado. Después, una diferencia de plata se corrige con un arqueo."
    >
      {confirmar ? (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void arrancar()} cargando={guardando}>
            Sí, arrancar
          </Button>
          <Button variant="outline" onClick={() => setConfirmar(false)}>
            Todavía no
          </Button>
        </div>
      ) : (
        <Button onClick={() => setConfirmar(true)}>
          <Rocket aria-hidden="true" />
          Arrancar
        </Button>
      )}
    </Paso>
  );
}

function Listo({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="flex items-center gap-2 text-sm">
      <CircleCheck className="h-4 w-4 text-success" aria-hidden="true" />
      {children}
    </p>
  );
}
